import React, { useState } from "react";
import { motion } from "framer-motion";

const tabClasses = {
  xs: "py-1 px-1 text-xs",
  "xs-sm": "py-1.5 px-2 text-[14px]",
  sm: "py-2 px-3 text-sm",
  md: "py-2 px-4 text-md",
  lg: "py-2 px-5 text-lg",
  xl: "py-2 px-6 text-xl",
};

const iconSize = {
  xs: "text-xs",
  "xs-sm": "text-[14px]",
  sm: "text-sm",
  md: "text-md",
  lg: "text-lg",
  xl: "text-xl",
};

const tabColor = {
  primary: {
    contained: "bg-primary-500 border-[1px] border-primary-500 text-white",
    outlined: "border-[1px] bg-primary-50  border-primary-500 text-primary-500",
  },
  warning: {
    contained: "bg-warning-500 border-[1px] border-warning-500 text-white",
    outlined: "border-[1px] bg-warning-50 border-warning-500 text-warning-500",
  },
  success: {
    contained: "bg-success-500 border-[1px] border-success-500 text-white",
    outlined: "border-[1px] bg-success-50 border-success-500 text-success-500",
  },
  danger: {
    contained: "bg-danger-500 border-[1px] border-danger-500 text-white",
    outlined: "border-[1px] bg-danger-50 border-danger-500 text-danger-500",
  },
  info: {
    contained: "bg-info-500 border-[1px] border-info-500 text-white",
    outlined: "border-[1px] bg-info-50 border-info-500 text-info-500",
  },
  black: {
    contained: "bg-black border-[1px] border-black text-white",
    outlined: "border-[1px] border-black text-black",
  },
  white: {
    contained: "bg-gray-50 border-[1px] border-white text-black",
    outlined: "border-[1px] border-gray-200 bg-gray-50 text-black",
  },
};
const tabActiveColor = {
  primary: {
    contained: "bg-primary-500 border-[1px] border-white text-white",
    outlined: "border-[1px] bg-primary-50  border-gray-200 text-gray-700",
  },
  warning: {
    contained: "bg-warning-500 border-[1px] border-warning-500 text-white",
    outlined: "border-[1px] bg-warning-50 border-warning-500 text-warning-500",
  },
  success: {
    contained: "bg-success-500 border-[1px] border-success-500 text-white",
    outlined: "border-[1px] bg-success-50 border-success-500 text-success-500",
  },
  danger: {
    contained: "bg-danger-500 border-[1px] border-danger-500 text-white",
    outlined: "border-[1px] bg-danger-50 border-danger-500 text-danger-500",
  },
  info: {
    contained: "bg-info-500 border-[1px] border-info-500 text-white",
    outlined: "border-[1px] bg-info-50 border-info-500 text-info-500",
  },
  black: {
    contained: "bg-black border-[1px] border-black text-white",
    outlined: "border-[1px] border-black text-black",
  },
  white: {
    contained: "bg-gray-50 border-[1px] border-white text-black",
    outlined: "border-[1px] border-gray-200 bg-gray-50 text-black",
  },
};

const AccordianNew = ({
  data,
  openDefault = 0, // 0 for first open, null to close all, -1 to open all
  fullWidth = true,
  variant = "contained", // contained, outlined
  size = "md", // xs, xs-sm, sm, md, lg, xl
  color = "primary", // primary, warning, success, danger, info, black, white
  activeColor = "black", // primary, warning, success, danger, info, black, white
  icon = "stat_minus_1",
  iconPosition = "left", // left, right
  bodyGap = true,
}) => {
  // Initialize openIndices based on openDefault
  const [openIndices, setOpenIndices] = useState(() => {
    if (openDefault === null) return []; // All closed
    if (openDefault === -1) return data.map((_, index) => index); // All open
    return [openDefault]; // Specific index open
  });

  // Toggle the open state of an item
  const toggleIndex = (index) => {
    setOpenIndices((prev) => {
      if (prev.includes(index)) {
        // If index is already open, remove it (close the item)
        return prev.filter((i) => i !== index);
      } else {
        // If index is not open, add it (open the item)
        return [...prev, index];
      }
    });
  };

  return (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-1 flex-1">
      {data.map((item, index) => {
        const isOpen = openIndices.includes(index);
        return (
          <div
            key={index} // Added key for React list rendering
            className={`flex flex-col ${isOpen || fullWidth ? "w-full" : ""}`}
          >
            <div
              onClick={() => toggleIndex(index)}
              className={`cursor-pointer rounded flex items-center justify-between gap-2 ${
                isOpen
                  ? tabActiveColor[activeColor][variant]
                  : tabColor[color][variant]
              }  ${tabClasses[size]}`}
            >
              <div
                className={`flex items-center justify-between gap-2 ${
                  iconPosition === "right" ? "ml-2" : "mr-2"
                }`}
              >
                {iconPosition === "left" && icon && (
                  <span
                    className={`material-symbols-outlined transition-transform duration-300 ${
                      iconSize[size]
                    } ${isOpen ? "" : "rotate-180"}`}
                  >
                    {icon}
                  </span>
                )}
                {item.title}
              </div>
              {iconPosition === "right" && icon && (
                <span
                  className={`material-symbols-outlined transition-transform duration-300 ${
                    iconSize[size]
                  } ${isOpen ? "" : "rotate-180"}`}
                >
                  {icon}
                </span>
              )}
            </div>
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{
                height: isOpen ? "auto" : 0,
                opacity: isOpen ? 1 : 0,
              }}
              transition={{ duration: 0.1, ease: "easeInOut" }}
              className={`overflow-hidden  border-[1px] border-t-0 border-gray-200 ${
                bodyGap && "px-5"
              }`}
            >
              {isOpen && (
                <div className={`${bodyGap && "py-2"}`}>{item.content}</div>
              )}
            </motion.div>
          </div>
        );
      })}
    </div>
  );
};

export default AccordianNew;
